/**
 * Quick Book modal — Track D, 2026-05-11.
 *
 * Receptionist-facing single-form modal for creating a walk-in booking
 * without leaving the dashboard. Closes Gap D from the 2026-05-11
 * parity matrix.
 *
 * Interface contract (agreed with Track A): the integrating page
 * imports this component and renders it with `apiUrl`, `open`,
 * `onClose`, and `onBooked(bookingId)`. The `apiUrl` prop is required
 * because the modal lives outside any page-level config context; the
 * dashboard already has `config.deployment.apiUrl` in scope.
 *
 * Form flow:
 *   1. Customer search (debounced 300ms, ≥2 chars, up to 10 results)
 *   2. Service type radio: Daycare / Boarding / Grooming
 *   3. Date + start time (boarding adds end-date + end-time)
 *   4. Dog multi-select (from the customer's dogs)
 *   5. Submit → POST /api/admin/bookings → onBooked(newBookingId)
 *
 * Server validation surfaces as either a field-scoped error (Zod
 * validation details) or a top-level error banner.
 *
 * #679 — walk-in handoff: when a search comes back EMPTY, the modal offers a
 * real "+ Create new customer" that routes to the shipped #654 flow
 * (`/customers?create=1&q=<searched text>` → create modal prefilled → detail
 * page → "+ Add a pet"), replacing the old disabled "coming soon" stub that
 * hid a capability which exists (Principle 2: the next action is always
 * obvious — an empty state carries its primary action). The affordance is
 * UX-gated on `customers:write:all` — the exact permission the server
 * enforces on POST /api/admin/crm/customers — via the #653 pattern
 * (`rolesHavePermission` + `effectiveRolesOf`, #428 multi-role aware), so a
 * role the server would 403 (accountant, floor staff) never sees a dead
 * button. NOTE: this adds a react-router dependency — the modal must render
 * inside a Router (AppShell, its only integrator, does).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  adminBookingsApi,
  adminLodgingAssignApi,
  api,
  fetchServiceTypes,
  readSession,
  todayIsoDate,
  type AdminCustomerDog,
  type ApiResponse,
  type GroomDayDaycareOffer,
  type HeatAnswer,
  type HeatDeclarations,
  type LodgingUnit,
  type ServiceTypeSummary,
  type TransportMode,
  TRANSPORT_MODE_LABELS,
} from '../lib/api.js';
import { effectiveRolesOf, rolesHavePermission } from '../lib/rbac-matrix.js';
// Sawyer 09 pet-inactivity (D1)
import { AssessmentOverrideField, acceptedOverride } from './booking/AssessmentOverrideField.js';
// Ted #28 Phase 2 (#1050) — Gingr's "reservation creation alert", non-blocking:
// each dog's flags beside it in the picker, the owner's under the customer,
// and one "Before you book" panel naming what alerts at creation (INTACT,
// an unsigned agreement, lapsed shots). It never disables Book.
import { DogFlagChips } from './dog-first/index.js';
import { flagsAlertingAt, uniqueByKey } from './dog-first/flag-alerts.js';
import type { DogFlag } from '../lib/api.js';
// Ted #30 (#1109, lane LR) — "Make it recurring": the same panel swaps to the
// series controls (no modal-on-modal); the draft the desk already entered
// becomes the series' first date and values.
import { RecurringBookingDialog } from './booking/RecurringBookingDialog.js';
// Sawyer 07 quickbook-bed-addons (B1 C1) — the bed while booking, and the extras on the same card.
import { BookingBedField } from './booking/BookingBedField.js';
import { AdditionalServicesField } from './booking/AdditionalServicesField.js';
import { useLodgingRead } from './booking/useLodgingRead.js';
import { bedReadRange, canPlaceBeds, draftPlacement } from '../lib/booking-bed.js';
import { bedPickerState } from '../lib/lodging-moves.js';
import { postBookedNotes as notesAfterBooking, type PostBookedNote, type SelectedService } from '../lib/quick-book-outcome.js';
import type { QuickBookCreateResponse } from '../lib/quick-book-services-api.js';
import './QuickBookModal.css';

export interface QuickBookModalProps {
  apiUrl: string;
  open: boolean;
  onClose: () => void;
  onBooked: (bookingId: string) => void;
}

type ServiceKey = 'daycare' | 'boarding' | 'grooming';

interface CustomerHit {
  id: string;
  name: string;
  email: string;
  phone: string;
  /**
   * #547 — active dog names for this customer (server sends `dogs`, the
   * #328 A#4 dog-first field). Walk-ins are looked up by the DOG's name
   * ('emphasis on the dog', 2026-07-21 walkthrough), so the dropdown
   * leads with the pet and drops the owner to a secondary line.
   */
  dogs: string[];
}

interface FieldError {
  field: string;
  message: string;
}

const SERVICE_OPTIONS: ReadonlyArray<{ key: ServiceKey; label: string }> = [
  { key: 'daycare', label: 'Daycare' },
  { key: 'boarding', label: 'Boarding' },
  { key: 'grooming', label: 'Grooming' },
];

function roundedNextHour(now: Date = new Date()): string {
  const hour = (now.getHours() + 1) % 24;
  return `${String(hour).padStart(2, '0')}:00`;
}

function isoDateOffset(days: number, base: Date = new Date()): string {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function QuickBookModal({
  apiUrl,
  open,
  onClose,
  onBooked,
}: QuickBookModalProps): JSX.Element | null {
  const [customerQuery, setCustomerQuery] = useState('');
  const [customers, setCustomers] = useState<CustomerHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchEmpty, setSearchEmpty] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState<CustomerHit | null>(null);
  const [customerDogs, setCustomerDogs] = useState<AdminCustomerDog[]>([]);
  // Ted #28 Phase 2 — the OWNER's flags for the selected customer (absent on
  // a server older than Phase 2 ⇒ none, never a guessed prompt).
  const [accountFlags, setAccountFlags] = useState<DogFlag[]>([]);
  const [dogsLoading, setDogsLoading] = useState(false);
  const [selectedDogIds, setSelectedDogIds] = useState<string[]>([]);
  // LG (#1111) — "Is your dog currently in heat?", asked for the selected
  // dogs the SERVER says need it (`needs_heat_question`), answered per dog.
  // A Yes shows Ted's notice and blocks Book; the server refuses it anyway.
  const [heatDeclarations, setHeatDeclarations] = useState<HeatDeclarations>({});
  const [heatCopy, setHeatCopy] = useState<{ label: string; help: string; notice: string } | null>(null);
  // LG (board Contracts §3) — the 201 landed HELD for staff review: the
  // modal says so instead of closing quietly (the strong flag on this surface).
  const [postBookedHold, setPostBookedHold] = useState<{ bookingId: string; reason: string } | null>(null);
  // Ted #34 E6.1 (#942) — the shop's groom-day half-day, read with the dogs
  // (null = not offered here), and whether the desk ticked it.
  const [groomDayOffer, setGroomDayOffer] = useState<GroomDayDaycareOffer | null>(null);
  const [addHalfDay, setAddHalfDay] = useState(false);
  // The groom is booked but the half-day lost a race after the pre-flight:
  // the modal says so and waits for Done instead of closing quietly.
  const [postBookedHalfDayNote, setPostBookedHalfDayNote] = useState<{ bookingId: string } | null>(null);
  // Ted #30 (lane LR) — the panel is showing the recurring controls.
  const [recurring, setRecurring] = useState(false);

  const [serviceKey, setServiceKey] = useState<ServiceKey>('daycare');
  const [serviceTypes, setServiceTypes] = useState<ServiceTypeSummary[]>([]);

  const [date, setDate] = useState<string>(() => todayIsoDate());
  const [startTime, setStartTime] = useState<string>(() => roundedNextHour());
  const [endDate, setEndDate] = useState<string>(() => isoDateOffset(1));
  const [endTime, setEndTime] = useState<string>(() => roundedNextHour());
  const [notes, setNotes] = useState<string>('');
  // Ted #28 (#1050) — transport on the stay. 'none' is the default and is
  // not sent (the column default already says so).
  const [transport, setTransport] = useState<TransportMode>('none');
  // Ted #12 — "Send notification to client — Yes/No", separate from the
  // tenant's reminder settings. Defaults follow the date: ON for today or
  // later, OFF for a past date (entered after the fact — a confirmation
  // for a visit that already happened is exactly what Ted does not want).
  // Once the receptionist touches the box, the date stops driving it.
  const [notifyCustomer, setNotifyCustomer] = useState<boolean>(true);
  const [notifyTouched, setNotifyTouched] = useState(false);
  // Sawyer 07 quickbook-bed-addons (B1 C1)
  // The bed picked while booking (Boarding only; never sent in the create).
  const [bedUnit, setBedUnit] = useState<LodgingUnit | null>(null);
  // The extras ticked on the card, and whether the server answered the quote
  // (only then may the create carry them — AC15).
  const [services, setServices] = useState<SelectedService[]>([]);
  const [servicesAvailable, setServicesAvailable] = useState(false);
  // Booked, but the bed or a service did not land: the card stays open and
  // says so (one Done), instead of closing quietly.
  const [postBooked, setPostBooked] = useState<{ bookingId: string; notes: PostBookedNote[] } | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Sawyer 09 pet-inactivity (D1) — the #718 override, LATCHED once the server asks for it.
  const [assessmentHeld, setAssessmentHeld] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const customerSearchInputRef = useRef<HTMLInputElement | null>(null);
  const dialogRef = useRef<HTMLFormElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // ── Reset on close ───────────────────────────────────────────────
  useEffect(() => {
    if (open) return;
    setCustomerQuery('');
    setCustomers([]);
    setSelectedCustomer(null);
    setCustomerDogs([]);
    setSelectedDogIds([]);
    setServiceKey('daycare');
    setDate(todayIsoDate());
    setStartTime(roundedNextHour());
    setEndDate(isoDateOffset(1));
    setEndTime(roundedNextHour());
    setNotes('');
    setSubmitting(false);
    setError(null);
    setAssessmentHeld(false); // Sawyer 09 pet-inactivity (D1)
    setOverrideReason('');
    setFieldErrors({});
    setSearchEmpty(false);
    setHeatDeclarations({});
    setPostBookedHold(null);
    // Sawyer 07 quickbook-bed-addons (B1 C1)
    setBedUnit(null);
    setServices([]);
    setServicesAvailable(false);
    setPostBooked(null);
  }, [open]);

  // ── Esc closes modal ─────────────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // ── Initial focus and restoration ───────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const t = window.setTimeout(() => {
      customerSearchInputRef.current?.focus();
    }, 50);
    return () => {
      window.clearTimeout(t);
      restoreFocusRef.current?.focus();
    };
  }, [open]);

  const handleDialogKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLFormElement>): void => {
      if (event.key !== 'Tab') return;
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [],
  );

  // ── Load service types (once per open) ───────────────────────────
  useEffect(() => {
    if (!open) return;
    const { token } = readSession();
    if (!token) return;
    let cancelled = false;
    void fetchServiceTypes(apiUrl, token)
      .then((list) => {
        if (!cancelled) setServiceTypes(list);
      })
      .catch(() => {
        // Non-fatal — Submit will surface a server-side service-not-found error.
      });
    return () => {
      cancelled = true;
    };
  }, [open, apiUrl]);

  // ── Customer search debounce ─────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    if (selectedCustomer) return undefined;
    const q = customerQuery.trim();
    if (q.length < 2) {
      setCustomers([]);
      setSearchEmpty(false);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      type SearchResponse = {
        customers: Array<{
          id: string;
          name: string;
          email: string;
          phone: string;
          dogs?: string[];
        }>;
      };
      const params = new URLSearchParams({
        search: q,
        segment: 'all',
        limit: '10',
        offset: '0',
      });
      const result: ApiResponse<SearchResponse> = await api.get<SearchResponse>(
        apiUrl,
        `/api/admin/crm/customers?${params.toString()}`,
      );
      if (cancelled) return;
      setSearching(false);
      if (result.data) {
        const hits = result.data.customers.map((c) => ({
          id: c.id,
          name: c.name,
          email: c.email,
          phone: c.phone,
          dogs: Array.isArray(c.dogs) ? c.dogs : [],
        }));
        setCustomers(hits);
        setSearchEmpty(hits.length === 0);
      } else {
        setCustomers([]);
        setSearchEmpty(true);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, customerQuery, selectedCustomer, apiUrl]);

  // ── Load dogs when customer selected ─────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    if (!selectedCustomer) {
      setCustomerDogs([]);
      setSelectedDogIds([]);
      return undefined;
    }
    let cancelled = false;
    setDogsLoading(true);
    void adminBookingsApi
      .getCustomerDogs(apiUrl, selectedCustomer.id)
      .then((res) => {
        if (cancelled) return;
        setDogsLoading(false);
        if (res.data) {
          setCustomerDogs(res.data.dogs);
          setAccountFlags(res.data.account?.flags ?? []);
          setHeatCopy(res.data.policies?.heat_question ?? null);
          setHeatDeclarations({});
          setGroomDayOffer(res.data.groom_day_daycare ?? null);
          // #688 — a customer with exactly ONE dog has no choice to make, so
          // asking for one is pure friction on the walk-in path (HTHD takes
          // walk-ins 2–3×/hour). Pre-select it. Deliberately NOT extended to
          // "select all" for multi-dog customers: booking the wrong dog is a
          // real error, and with 2+ dogs there IS a decision to make.
          if (res.data.dogs.length === 1) {
            setSelectedDogIds([res.data.dogs[0]!.id]);
          }
        } else {
          setCustomerDogs([]);
          setAccountFlags([]);
          setGroomDayOffer(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectedCustomer, apiUrl]);

  // Ted #28 Phase 2 — what alerts at CREATION for the dogs being booked and
  // their owner, deduped (a family shares the owner's flags). Non-blocking.
  const creationAlerts = useMemo(() => {
    const dogFlags = customerDogs
      .filter((d) => selectedDogIds.includes(d.id))
      .flatMap((d) => (d.flags ?? []).map((f) => ({ dog: d.name, flag: f })));
    const perDog = flagsAlertingAt(dogFlags.map((x) => x.flag), 'creation').map((flag) => ({
      flag,
      dog: dogFlags.find((x) => x.flag === flag)?.dog ?? null,
    }));
    const perOwner = uniqueByKey(flagsAlertingAt(accountFlags, 'creation')).map((flag) => ({ flag, dog: null as string | null }));
    return [...perOwner, ...perDog];
  }, [customerDogs, selectedDogIds, accountFlags]);

  const handleSelectCustomer = useCallback((c: CustomerHit) => {
    setSelectedCustomer(c);
    setCustomerQuery('');
    setCustomers([]);
    setSearchEmpty(false);
  }, []);

  const handleClearCustomer = useCallback(() => {
    setSelectedCustomer(null);
    setSelectedDogIds([]);
    setCustomerDogs([]);
    setAccountFlags([]);
    setHeatDeclarations({});
    // Sawyer 07 quickbook-bed-addons (B1 C1)
    setBedUnit(null);
    setServices([]);
    setServicesAvailable(false);
  }, []);

  // LG (#1111) — the selected dogs the server will ask about, in picker order.
  const heatDogs = useMemo(
    () => customerDogs.filter((d) => selectedDogIds.includes(d.id) && d.needs_heat_question === true),
    [customerDogs, selectedDogIds],
  );
  const heatAnswered = heatDogs.every((d) => heatDeclarations[d.id] === 'not_in_heat');
  const heatYes = heatDogs.some((d) => heatDeclarations[d.id] === 'in_heat');
  const setHeat = useCallback((dogId: string, answer: HeatAnswer) => {
    setHeatDeclarations((prev) => ({ ...prev, [dogId]: answer }));
  }, []);

  // #679 — can this session reach the #654 create flow? Mirrors the SERVER
  // gate on POST /api/admin/crm/customers (customers:write:all); an
  // affordance the server would 403 must not render at all (#665 class).
  const { staff } = readSession();
  const canCreateCustomer = rolesHavePermission(
    effectiveRolesOf(staff),
    'customers:write:all',
  );

  const navigate = useNavigate();
  // #679 — hand the searched text to the working #654 flow. Close FIRST
  // (the reset-on-close effect clears modal state), then route; the
  // Customers page opens its create modal with the query prefilled.
  const handleCreateCustomer = useCallback((): void => {
    const q = customerQuery.trim();
    onClose();
    navigate(`/customers?create=1&q=${encodeURIComponent(q)}`);
  }, [customerQuery, navigate, onClose]);

  const toggleDog = useCallback((dogId: string) => {
    setSelectedDogIds((prev) =>
      prev.includes(dogId) ? prev.filter((id) => id !== dogId) : [...prev, dogId],
    );
  }, []);

  const selectedServiceType = useMemo(
    () => serviceTypes.find((s) => s.name === serviceKey),
    [serviceTypes, serviceKey],
  );

  const isBoarding = serviceKey === 'boarding';

  // Sawyer 07 quickbook-bed-addons (B1 C1)
  // ── The bed, for whoever may place one ──────────────────────────────
  // The assign write AND a lodging read (route map /assign + /lodging); a
  // floor role never sees the field and never sends the read (AC5).
  const sessionRoles = effectiveRolesOf(readSession().staff);
  const canAssignBeds = canPlaceBeds(sessionRoles);
  // Refuter C-11 — who can add beds decides the no-beds sentence (lane 04's
  // narrow lodging write, or the whole Settings write).
  const canEditBeds =
    rolesHavePermission(sessionRoles, 'business-config.lodging:write:all') ||
    rolesHavePermission(sessionRoles, 'business-config:write:all');
  // C1 — the create's own gate (route map POST / ).
  const canAddServices = rolesHavePermission(sessionRoles, 'bookings.daycare-boarding:write:all');
  const hasParty = selectedCustomer !== null && selectedDogIds.length > 0;
  const overnight = Boolean(date && endDate && endDate > date);
  const bedPlacement = useMemo(
    () =>
      selectedCustomer
        ? draftPlacement({ customerId: selectedCustomer.id, dogCount: selectedDogIds.length, startDate: date, endDate })
        : null,
    [selectedCustomer, selectedDogIds.length, date, endDate],
  );
  const bedRead = useLodgingRead(
    apiUrl,
    open && canAssignBeds && isBoarding && hasParty && overnight ? bedReadRange(date, endDate) : null,
  );
  // A bed is only for Boarding, and only while the new dates still offer it.
  useEffect(() => {
    if (!isBoarding || !hasParty || !overnight) setBedUnit(null);
  }, [isBoarding, hasParty, overnight]);
  useEffect(() => {
    if (!bedUnit || !bedRead.data || !bedPlacement) return;
    const picker = bedPickerState(bedRead.data.units, bedPlacement, bedRead.data.stays);
    if (picker.kind !== 'offer' || !picker.beds.some((b) => b.id === bedUnit.id)) setBedUnit(null);
  }, [bedRead.data, bedPlacement, bedUnit]);
  const selectedDogLabel = customerDogs
    .filter((d) => selectedDogIds.includes(d.id))
    .map((d) => d.name)
    .join(' & ');
  // Ted #34 E6.1 — only a groom carries the half-day (no full day with a groom).
  const halfDayOffered = serviceKey === 'grooming' && groomDayOffer !== null;
  const halfDayChosen = halfDayOffered && addHalfDay;

  // #688 — a disabled control must state its own precondition
  // (docs/DESIGN-PHILOSOPHY.md). Before this, "Create booking" simply greyed
  // out and the reason was a section heading the eye skips past — the person
  // hitting it Wednesday is new to the system, and a silent block reads as a
  // broken form rather than an incomplete one.
  //
  // The reason and the disabled state are ONE expression on purpose. Keeping
  // them as two parallel conditionals is how a hint drifts out of sync with
  // the control it explains, leaving copy that confidently names the wrong
  // missing field. Add a precondition here and the message comes with it.
  // Ordered top-to-bottom to match the form, so the hint names the FIRST
  // thing the eye will find on the way back up.
  const blockedReason: string | null = !selectedCustomer
    ? 'Search for and select a customer first.'
    : !selectedServiceType
      ? 'Choose a service.'
      : !date
        ? 'Pick a date.'
        : isBoarding && !endDate
          ? 'Boarding needs a check-out date.'
          : selectedDogIds.length === 0
            ? // Only reachable with 2+ dogs — a single dog is auto-selected
              // above, and zero dogs already renders its own "No dogs on
              // file" hint in the Dogs section.
              'Select at least one dog.'
            : heatYes
              ? // LG (#1111) — Ted's hard stop, stated where the desk can act on it.
                (heatCopy?.notice ?? 'We do not accept female dogs who are in heat.')
              : !heatAnswered
                ? `Answer "${heatCopy?.label ?? 'Is your dog currently in heat?'}" for ${heatDogs
                    .filter((d) => heatDeclarations[d.id] !== 'not_in_heat')
                    .map((d) => d.name)
                    .join(', ')}.`
                : null;

  const submitDisabled = submitting || blockedReason !== null;
  // Sawyer 07 quickbook-bed-addons (B1 C1) — refuter C-2 — once a 201 is on screen (held, half-day lost, or a bed /
  // service that did not land), nothing may create a SECOND booking.
  const postBookedShowing = postBookedHold !== null || postBookedHalfDayNote !== null || postBooked !== null;

  // Ted #13a — a past date is allowed for the desk (the server keeps the
  // rule for customer self-booking). The date input no longer clamps to
  // today; the hint below the field says what a past date means.
  const isPastDate = date < todayIsoDate();
  useEffect(() => {
    if (!notifyTouched) setNotifyCustomer(!isPastDate);
  }, [isPastDate, notifyTouched]);
  useEffect(() => {
    if (!open) {
      setRecurring(false);
      // Ted #34 — a fresh Quick Book never inherits the last booking's half-day.
      setAddHalfDay(false);
      setPostBookedHalfDayNote(null);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (submitDisabled || postBookedShowing || !selectedCustomer || !selectedServiceType) return;
    setSubmitting(true);
    setError(null);
    setFieldErrors({});
    const override = acceptedOverride(overrideReason); // Sawyer 09 pet-inactivity (D1)

    // Sawyer 07 quickbook-bed-addons (B1 C1) — services only when the server answered the quote (AC15); `everyDay`
    // only on a stay. Never a price: the server prices.
    const servicesToSend =
      canAddServices && servicesAvailable
        ? services.map((sv) => ({
            ...(sv.kind === 'addOn' ? { addOnId: sv.id } : { serviceTypeId: sv.id }),
            ...(isBoarding && sv.everyDay ? { everyDay: true } : {}),
          }))
        : [];
    const bedToAssign = canAssignBeds && isBoarding && overnight ? bedUnit : null;

    const payload = isBoarding
      ? {
          customerId: selectedCustomer.id,
          serviceTypeId: selectedServiceType.id,
          dogIds: selectedDogIds,
          startDate: date,
          endDate,
          startTime,
          notes: notes || undefined,
          transport: transport === 'none' ? undefined : transport,
          notifyCustomer,
          ...(override ? { assessmentOverrideReason: override } : {}), // Sawyer 09 pet-inactivity (D1)
          ...(heatDogs.length > 0 ? { heatDeclarations } : {}),
          ...(servicesToSend.length > 0 ? { additionalServices: servicesToSend } : {}),
        }
      : {
          customerId: selectedCustomer.id,
          serviceTypeId: selectedServiceType.id,
          dogIds: selectedDogIds,
          date,
          startTime,
          notes: notes || undefined,
          transport: transport === 'none' ? undefined : transport,
          notifyCustomer,
          ...(override ? { assessmentOverrideReason: override } : {}), // Sawyer 09 pet-inactivity (D1)
          ...(heatDogs.length > 0 ? { heatDeclarations } : {}),
          ...(halfDayChosen ? { addHalfDayDaycare: true } : {}),
          ...(servicesToSend.length > 0 ? { additionalServices: servicesToSend } : {}),
        };

    const res = await adminBookingsApi.create(apiUrl, payload);

    if (res.data) {
      // Sawyer 07 quickbook-bed-addons (B1 C1) — the bed goes through the calendar's own write path
      // (same-family rule, advisory lock, gist constraint), once, after the
      // booking stands. A refusal never costs the desk the booking (AC3).
      let bedError: string | null = null;
      if (bedToAssign) {
        const assigned = await adminLodgingAssignApi.assign(apiUrl, {
          bookingId: res.data.booking.id,
          unitId: bedToAssign.id,
          startDate: date,
          endDate,
        });
        if (assigned.error) bedError = assigned.error;
      }
      setSubmitting(false);
      const notes = notesAfterBooking({
        dogLabel: selectedDogLabel || 'The dog',
        bed: { asked: bedToAssign !== null, error: bedError },
        services: { asked: servicesToSend.length > 0 ? services : [], response: (res.data as QuickBookCreateResponse).additional_services },
      });
      if (notes.length > 0) setPostBooked({ bookingId: res.data.booking.id, notes });
      // LG (board Contracts §3) — a HELD reservation stays open on screen with
      // the reason: the desk must know it is pending a manager, not booked.
      // `onBooked` is the integrator's "close + refresh" (AppShell), so it is
      // deferred to the panel's Done button — the desk reads the reason first.
      if (res.data.review_hold?.reason) {
        setPostBookedHold({ bookingId: res.data.booking.id, reason: res.data.review_hold.reason });
        return;
      }
      // Ted #34 E6.1 — asked for the half-day, got the groom only (a race
      // lost after the pre-flight): say so before closing.
      if (halfDayChosen && !res.data.half_day_daycare) {
        setPostBookedHalfDayNote({ bookingId: res.data.booking.id });
        return;
      }
      if (notes.length > 0) return;
      onBooked(res.data.booking.id);
      onClose();
      return;
    }
    setSubmitting(false);

    if (Array.isArray(res.details)) {
      const next: Record<string, string> = {};
      for (const raw of res.details) {
        const d = raw as FieldError;
        if (d && typeof d.field === 'string' && typeof d.message === 'string') {
          next[d.field] = d.message;
        }
      }
      setFieldErrors(next);
    }
    // Sawyer 09 pet-inactivity (D1) — the server's sentence stays the error; the field latches on.
    if (res.code === 'assessment_required') setAssessmentHeld(true);
    setError(res.error ?? 'Unable to create booking');
  };

  if (!open) return null;

  // Ted #30 (lane LR) — the recurring step replaces the form in the SAME
  // panel (DESIGN-PHILOSOPHY: no modal-on-modal). Single-day services only:
  // a series of multi-night stays is not this lane's.
  if (recurring && selectedCustomer && selectedServiceType && !isBoarding) {
    return (
      <div className="ferro-quickbook__backdrop" data-testid="quickbook-overlay">
        <form ref={dialogRef} className="ferro-quickbook" role="dialog" aria-modal="true" aria-labelledby="ferro-quickbook-title" onKeyDown={handleDialogKeyDown} onSubmit={(e) => e.preventDefault()} style={{ position: 'relative' }}>
          <header className="ferro-quickbook__header">
            <h2 id="ferro-quickbook-title" className="ferro-quickbook__title">Quick Book — recurring</h2>
            <button type="button" className="ferro-quickbook__close" onClick={onClose} aria-label="Close">×</button>
          </header>
          <RecurringBookingDialog
            apiUrl={apiUrl}
            draft={{
              customer: { id: selectedCustomer.id, name: selectedCustomer.name },
              serviceType: selectedServiceType,
              dogs: customerDogs.filter((d) => selectedDogIds.includes(d.id)),
              startDate: date,
              ...(startTime ? { startTime } : {}),
              ...(notes ? { notes } : {}),
              transport,
              heatDeclarations: heatDogs.length > 0 ? heatDeclarations : {},
              notifyCustomer,
              // Sawyer 09 pet-inactivity (D1, R8) — a reason typed on the single form rides into the series.
              ...(acceptedOverride(overrideReason) ? { assessmentOverrideReason: acceptedOverride(overrideReason) } : {}),
            }}
            onBack={() => setRecurring(false)}
            onDone={(_seriesId, firstBookingId) => {
              if (firstBookingId) onBooked(firstBookingId);
              onClose();
            }}
          />
        </form>
      </div>
    );
  }

  return (
    <div
      className="ferro-quickbook__backdrop"
      data-testid="quickbook-overlay"
    >
      <button
        type="button"
        aria-label="Close Quick Book"
        data-testid="quickbook-backdrop"
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          padding: 0,
          border: 0,
          background: 'transparent',
          cursor: 'default',
        }}
      />
      <form
        ref={dialogRef}
        className="ferro-quickbook"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ferro-quickbook-title"
        onKeyDown={handleDialogKeyDown}
        onSubmit={handleSubmit}
        style={{ position: 'relative' }}
      >
        <header className="ferro-quickbook__header">
          <h2 id="ferro-quickbook-title" className="ferro-quickbook__title">
            Quick Book
          </h2>
          <button
            type="button"
            className="ferro-quickbook__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {/* Customer ------------------------------------------------ */}
        <section className="ferro-quickbook__section">
          <span className="ferro-quickbook__section-label">Customer</span>
          {selectedCustomer ? (
            <div className="ferro-quickbook__selected-customer" data-testid="selected-customer">
              <span className="ferro-quickbook__selected-name">
                {selectedCustomer.name}
                {/* Ted #28 Phase 2 — the owner's flags under the owner. */}
                <span className="ferro-quickbook__owner-flags">
                  <DogFlagChips flags={accountFlags} scope="account" testIdPrefix="quickbook-owner-" />
                </span>
              </span>
              <button
                type="button"
                className="ferro-quickbook__selected-clear"
                onClick={handleClearCustomer}
                aria-label="Clear customer selection"
              >
                ✕ Clear
              </button>
            </div>
          ) : (
            <>
              <input
                ref={customerSearchInputRef}
                type="search"
                className="ferro-field__input"
                placeholder="Search by name, phone, or email…"
                value={customerQuery}
                onChange={(e) => setCustomerQuery(e.target.value)}
                aria-label="Search customers"
              />
              {searching ? (
                <p className="ferro-quickbook__hint" role="status">
                  Searching…
                </p>
              ) : null}
              {customers.length > 0 ? (
                <ul
                  className="ferro-quickbook__results"
                  aria-label="Customer search results"
                >
                  {customers.map((c) => {
                    // #547 — DOG-first framing: pet name(s) prominent,
                    // owner (+ contact) secondary. No dogs on file → fall
                    // back to owner-name-prominent so a row is never blank.
                    const hasDogs = c.dogs.length > 0;
                    const primary = hasDogs ? c.dogs.join(', ') : c.name;
                    const secondary = hasDogs
                      ? [c.name, c.phone || c.email].filter(Boolean).join(' · ')
                      : c.phone || c.email || '';
                    return (
                    <li key={c.id}>
                      <button
                        type="button"
                        className="ferro-quickbook__result ferro-quickbook__result--petfirst"
                        onClick={() => handleSelectCustomer(c)}
                        aria-label={hasDogs ? `${primary} — owner ${c.name}` : c.name}
                      >
                        <span className="ferro-quickbook__result-name">{primary}</span>
                        {secondary ? (
                          <span className="ferro-quickbook__result-meta">
                            {secondary}
                          </span>
                        ) : null}
                      </button>
                    </li>
                    );
                  })}
                </ul>
              ) : null}
              {!searching && searchEmpty && customerQuery.trim().length >= 2 ? (
                <>
                  <p className="ferro-quickbook__hint">No customers match that search.</p>
                  {/* #679 — the empty state's primary action: a REAL door to
                      the shipped #654 walk-in flow, carrying the searched
                      text. Rendered only for roles the server admits to the
                      create POST — no dead buttons. */}
                  {canCreateCustomer ? (
                    <button
                      type="button"
                      className="ferro-quickbook__create-customer"
                      onClick={handleCreateCustomer}
                      data-testid="quickbook-create-customer"
                    >
                      + Create new customer
                    </button>
                  ) : null}
                </>
              ) : null}
              {customerQuery.trim().length < 2 ? (
                <p className="ferro-quickbook__hint">
                  Type at least 2 characters to search.
                </p>
              ) : null}
            </>
          )}
        </section>

        {/* Service type ------------------------------------------ */}
        <section className="ferro-quickbook__section">
          <span className="ferro-quickbook__section-label">Service</span>
          <div
            className="ferro-quickbook__service-grid"
            role="radiogroup"
            aria-label="Service type"
          >
            {SERVICE_OPTIONS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className="ferro-quickbook__service"
                data-selected={serviceKey === key}
                onClick={() => setServiceKey(key)}
                role="radio"
                aria-checked={serviceKey === key}
              >
                <span className="ferro-quickbook__service-label">{label}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Ted #34 E6.1 (#942) — "When I book a groom I can add a half-day
            of daycare at the groom-day rate." One visit: the half-day rides
            the groom. Shown only on a groom, only where the shop set a rate. */}
        {halfDayOffered && groomDayOffer ? (
          <section className="ferro-quickbook__section" data-testid="quickbook-half-day">
            <label className="ferro-field ferro-field--inline">
              <input
                type="checkbox"
                checked={addHalfDay}
                onChange={(e) => setAddHalfDay(e.target.checked)}
                data-testid="quickbook-half-day-toggle"
              />
              <span>
                Add half-day daycare — ${(groomDayOffer.rate_cents / 100).toFixed(2)} grooming-day rate
                {selectedDogIds.length > 1 ? ' per dog' : ''}
                <span className="ferro-quickbook__hint">
                  {' '}— one visit: the dog stays for daycare around the groom. No full day with a groom.
                </span>
              </span>
            </label>
          </section>
        ) : null}

        {/* Date & time ------------------------------------------- */}
        <section className="ferro-quickbook__section">
          <span className="ferro-quickbook__section-label">
            {isBoarding ? 'Stay dates' : 'Date'}
          </span>
          <div className="ferro-quickbook__row">
            <label className="ferro-field">
              <span className="ferro-field__label">
                {isBoarding ? 'Drop-off date' : 'Date'}
              </span>
              <input
                type="date"
                className="ferro-field__input"
                value={date}
                onChange={(e) => setDate(e.target.value || todayIsoDate())}
                aria-label={isBoarding ? 'Drop-off date' : 'Booking date'}
              />
              {fieldErrors.date || fieldErrors.startDate ? (
                <span className="ferro-quickbook__hint" role="alert">
                  {fieldErrors.date ?? fieldErrors.startDate}
                </span>
              ) : isPastDate ? (
                <span
                  className="ferro-quickbook__hint"
                  data-testid="quickbook-past-date-hint"
                >
                  Past date — entered after the fact. The customer is not
                  notified unless you tick the box below.
                </span>
              ) : null}
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">Start time</span>
              <input
                type="time"
                className="ferro-field__input"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                aria-label="Start time"
              />
              {fieldErrors.startTime ? (
                <span className="ferro-quickbook__hint" role="alert">
                  {fieldErrors.startTime}
                </span>
              ) : null}
            </label>
          </div>
          {isBoarding ? (
            <div className="ferro-quickbook__row" data-testid="boarding-end-row">
              <label className="ferro-field">
                <span className="ferro-field__label">Pick-up date</span>
                <input
                  type="date"
                  className="ferro-field__input"
                  value={endDate}
                  min={date}
                  onChange={(e) => setEndDate(e.target.value)}
                  aria-label="Pick-up date"
                />
                {fieldErrors.endDate ? (
                  <span className="ferro-quickbook__hint" role="alert">
                    {fieldErrors.endDate}
                  </span>
                ) : null}
              </label>
              <label className="ferro-field">
                <span className="ferro-field__label">Pick-up time</span>
                <input
                  type="time"
                  className="ferro-field__input"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  aria-label="Pick-up time"
                />
              </label>
            </div>
          ) : null}
        </section>

        {/* Dogs --------------------------------------------------- */}
        {selectedCustomer ? (
          <section className="ferro-quickbook__section">
            <span className="ferro-quickbook__section-label">
              Dogs ({selectedDogIds.length} selected)
            </span>
            {dogsLoading ? (
              <p className="ferro-quickbook__hint" role="status">
                Loading dogs…
              </p>
            ) : null}
            {!dogsLoading && customerDogs.length === 0 ? (
              <p className="ferro-quickbook__hint">
                No dogs on file for this customer.
              </p>
            ) : null}
            {!dogsLoading && customerDogs.length > 0 ? (
              <ul className="ferro-quickbook__dogs">
                {customerDogs.map((d) => {
                  const selected = selectedDogIds.includes(d.id);
                  return (
                    <li key={d.id}>
                      <button
                        type="button"
                        className="ferro-quickbook__dog"
                        data-selected={selected}
                        onClick={() => toggleDog(d.id)}
                        aria-pressed={selected}
                      >
                        <span>{selected ? '☑' : '☐'}</span>
                        <span>
                          <strong>{d.name}</strong>
                          {d.breed || d.sizeCategory ? (
                            <span className="ferro-quickbook__dog-meta">
                              {' · '}
                              {[d.breed, d.sizeCategory].filter(Boolean).join(' · ')}
                            </span>
                          ) : null}
                          {/* Ted #28 Phase 2 — INTACT / vax / meds beside the dog being booked. */}
                          <span className="ferro-quickbook__dog-flags">
                            <DogFlagChips flags={d.flags} scope="dog" testIdPrefix={`quickbook-${d.id}-`} />
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {fieldErrors.dogIds ? (
              <span className="ferro-quickbook__hint" role="alert">
                {fieldErrors.dogIds}
              </span>
            ) : null}
          </section>
        ) : null}

        {/* LG (#1111) — Ted's heat question, for the selected dogs the server
            says need it. One radio pair per dog, the dog's name in the legend. */}
        {heatDogs.length > 0 ? (
          <section className="ferro-quickbook__section ferro-quickbook__heat" data-testid="quickbook-heat">
            <span className="ferro-quickbook__section-label">
              {heatCopy?.label ?? 'Is your dog currently in heat?'}
            </span>
            <p className="ferro-quickbook__hint">
              {heatCopy?.help ?? 'Asked for every female who is not spayed. A Yes cannot be accepted.'}
            </p>
            {heatDogs.map((d) => {
              const name = `heat-${d.id}`;
              const answer = heatDeclarations[d.id];
              return (
                <fieldset key={d.id} className="ferro-quickbook__heat-dog" data-testid={`quickbook-heat-${d.id}`}>
                  <legend>{d.name}</legend>
                  <div className="ferro-quickbook__heat-options" role="radiogroup" aria-label={`${d.name}: ${heatCopy?.label ?? 'Is your dog currently in heat?'}`}>
                    <label className="ferro-quickbook__heat-option" htmlFor={`${name}-no`}>
                      <input type="radio" id={`${name}-no`} name={name} value="not_in_heat" checked={answer === 'not_in_heat'} onChange={() => setHeat(d.id, 'not_in_heat')} data-testid={`quickbook-heat-no-${d.id}`} />
                      <span>No</span>
                    </label>
                    <label className="ferro-quickbook__heat-option" htmlFor={`${name}-yes`}>
                      <input type="radio" id={`${name}-yes`} name={name} value="in_heat" checked={answer === 'in_heat'} onChange={() => setHeat(d.id, 'in_heat')} data-testid={`quickbook-heat-yes-${d.id}`} />
                      <span>Yes</span>
                    </label>
                  </div>
                </fieldset>
              );
            })}
            {heatYes ? (
              <div className="ferro-quickbook__error" role="alert" data-testid="quickbook-heat-notice">
                {heatCopy?.notice ?? 'We do not accept female dogs who are in heat.'}
              </div>
            ) : null}
          </section>
        ) : null}

        {/* Sawyer 07 quickbook-bed-addons (B1) — "when I'm booking, he's trying to select a bed over
            nights". Boarding only, for roles that may place a bed; never
            required (Create booking ignores it). */}
        {isBoarding && canAssignBeds ? (
          <section className="ferro-quickbook__section">
            <BookingBedField
              read={bedRead}
              hasParty={hasParty}
              overnight={overnight}
              placement={bedPlacement}
              value={bedUnit?.id ?? ''}
              onChange={setBedUnit}
              canEditBeds={canEditBeds}
              context="create"
              disabled={submitting || postBookedShowing}
            />
          </section>
        ) : null}

        {/* Sawyer 07 quickbook-bed-addons (C1) — "on that same QuickBook card, you have additional
            services". The server's list (what Settings has on), the server's
            price; hidden until the quote answers (deploy window, AC15). */}
        {canAddServices && selectedCustomer && selectedServiceType && selectedDogIds.length > 0 && date && (!isBoarding || (endDate && endDate >= date)) ? (
          <AdditionalServicesField
            apiUrl={apiUrl}
            customerId={selectedCustomer.id}
            date={date}
            endDate={isBoarding ? endDate : null}
            dogCount={selectedDogIds.length}
            mainServiceTypeId={selectedServiceType.id}
            value={services}
            onChange={setServices}
            onAvailability={setServicesAvailable}
            disabled={submitting || postBookedShowing}
          />
        ) : null}

        {/* Ted #12 — notify the client? ---------------------------- */}
        <section className="ferro-quickbook__section">
          <label className="ferro-field ferro-field--inline">
            <input
              type="checkbox"
              checked={notifyCustomer}
              onChange={(e) => {
                setNotifyTouched(true);
                setNotifyCustomer(e.target.checked);
              }}
              data-testid="quickbook-notify-customer"
            />
            <span>
              Send notification to client
              <span className="ferro-quickbook__hint">
                {/* Ted 10-09 (#915) — the server never emails a confirmation
                    about a visit that is already over; the hint says so
                    rather than promising one. */}
                {notifyCustomer && isPastDate
                  ? ' — this visit has already happened, so no email will go'
                  : notifyCustomer
                    ? ' — confirmation now, reminder the day before (if there is time)'
                    : ' — no email; the day-before reminder is not scheduled either'}
              </span>
            </span>
          </label>
        </section>

        {/* Ted #28 (#1050) — transport on the stay ----------------- */}
        <section className="ferro-quickbook__section">
          <label className="ferro-field">
            <span className="ferro-field__label">Transport</span>
            <select
              className="ferro-field__input"
              value={transport}
              onChange={(e) => setTransport(e.target.value as TransportMode)}
              data-testid="quickbook-transport"
            >
              {(Object.keys(TRANSPORT_MODE_LABELS) as TransportMode[]).map((mode) => (
                <option key={mode} value={mode}>
                  {TRANSPORT_MODE_LABELS[mode]}
                </option>
              ))}
            </select>
            <span className="ferro-quickbook__hint">
              {transport === 'arrival' || transport === 'both'
                ? 'Check-in will ask staff to confirm the dog is physically here.'
                : transport === 'departure'
                  ? 'Checkout will show the dog goes home by transport.'
                  : 'The flag follows the dog on every screen once set.'}
            </span>
          </label>
        </section>

        {/* Notes -------------------------------------------------- */}
        <section className="ferro-quickbook__section">
          <label className="ferro-field">
            <span className="ferro-field__label">Notes (optional)</span>
            <input
              type="text"
              className="ferro-field__input"
              value={notes}
              maxLength={1000}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything the staff should know"
            />
          </label>
        </section>

        {/* Ted #28 Phase 2 (#1050) — Gingr's reservation-creation alert. Ted:
            Gingr "gives you warning if you attempt to make a reservation and
            the client does not have a signed agreement/contract or the dog is
            not neutered. Those are two huge issues for the governing
            agencies." A heads-up, not a refusal (non-blocking until Ted says
            otherwise): Book stays enabled; the panel names each condition
            with its sentence so the desk acts before the dog is on the floor. */}
        {creationAlerts.length > 0 ? (
          <section
            className="ferro-quickbook__creation-alert"
            role="status"
            aria-label="Before you book"
            data-testid="quickbook-creation-alert"
          >
            <span className="ferro-quickbook__creation-alert-title">Before you book</span>
            <ul className="ferro-quickbook__creation-alert-list">
              {creationAlerts.map(({ flag, dog }) => (
                <li key={`${dog ?? 'owner'}:${flag.key}`} data-testid={`quickbook-creation-alert-${flag.key}`}>
                  <DogFlagChips flags={[flag]} scope={flag.scope === 'account' ? 'account' : 'dog'} testIdPrefix="quickbook-alert-" />
                  <span className="ferro-quickbook__creation-alert-text">
                    {dog ? `${dog}: ` : ''}
                    {flag.detail}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {error ? (
          <div className="ferro-quickbook__error" role="alert">
            {error}
          </div>
        ) : null}

        {/* Sawyer 09 pet-inactivity (D1) — the way through the meet-and-greet hold. */}
        {assessmentHeld ? (
          <AssessmentOverrideField value={overrideReason} onChange={setOverrideReason} disabled={submitting} />
        ) : null}

        {/* Ted #34 E6.1 — the groom is booked; the half-day could not be added. */}
        {postBookedHalfDayNote ? (
          <section
            className="ferro-quickbook__hold"
            role="alert"
            aria-label="Half-day daycare not added"
            data-testid="quickbook-half-day-not-added"
          >
            <span className="ferro-quickbook__hold-title">Groom booked — the half-day daycare was not added</span>
            <p className="ferro-quickbook__hint">
              Daycare filled up while you were booking. Add it from the visit (Add a service) if a spot opens.
            </p>
            <button
              type="button"
              className="ferro-btn ferro-btn--primary"
              onClick={() => {
                onBooked(postBookedHalfDayNote.bookingId);
                onClose();
              }}
              data-testid="quickbook-half-day-not-added-done"
            >
              Done
            </button>
          </section>
        ) : null}

        {/* Sawyer 07 quickbook-bed-addons (B1 C1) — booked, but the bed or a service did not land.
            Beside a hold / half-day panel the notes are plain lines (that
            panel keeps the one Done); alone they carry the Done. */}
        {postBooked ? (
          <section
            className="ferro-quickbook__hold"
            role="alert"
            aria-label="Booked — not everything was added"
            data-testid="quickbook-post-booked-notes"
          >
            {postBooked.notes.map((n) => (
              <div key={n.key} data-testid={`quickbook-post-booked-note-${n.key}`}>
                <span className="ferro-quickbook__hold-title">{n.title}</span>
                <p className="ferro-quickbook__hold-reason">{n.body}</p>
              </div>
            ))}
            {postBookedHold === null && postBookedHalfDayNote === null ? (
              <button
                type="button"
                className="ferro-btn ferro-btn--primary"
                onClick={() => {
                  onBooked(postBooked.bookingId);
                  onClose();
                }}
                data-testid="quickbook-post-booked-done"
              >
                Done
              </button>
            ) : null}
          </section>
        ) : null}

        {/* LG (board Contracts §3) — the strong flag: booked, but HELD. */}
        {postBookedHold ? (
          <section
            className="ferro-quickbook__hold"
            role="alert"
            aria-label="Needs staff review"
            data-testid="quickbook-review-hold"
          >
            <span className="ferro-quickbook__hold-title">Booked — needs staff review before it is confirmed</span>
            <p className="ferro-quickbook__hold-reason">{postBookedHold.reason}</p>
            <p className="ferro-quickbook__hint">
              It is in the approval queue as pending. A manager or owner confirms it with a reason; the client has not been told it is confirmed.
            </p>
            <button
              type="button"
              className="ferro-btn ferro-btn--primary"
              onClick={() => onBooked(postBookedHold.bookingId)}
              data-testid="quickbook-review-hold-done"
            >
              Done
            </button>
          </section>
        ) : null}

        <footer className="ferro-quickbook__actions">
          {/* #688 — the block states its own reason. role="status" so a
              screen reader hears WHY the button is unavailable; a disabled
              button is not focusable and would otherwise announce nothing. */}
          {blockedReason && !submitting ? (
            <span className="ferro-quickbook__blocked" role="status">
              {blockedReason}
            </span>
          ) : null}
          {/* Ted #30 (lane LR) — rendered only when the single booking could be
              created as it stands and the service is single-day. */}
          {/* Sawyer 07 quickbook-bed-addons (C1) — AC17: the series route carries no services yet. */}
          {!isBoarding && services.length > 0 && !postBookedShowing ? (
            <span className="ferro-quickbook__blocked" data-testid="quickbook-recurring-services-hint">
              Recurring bookings don't carry added services yet — add them to each visit.
            </span>
          ) : null}
          {!isBoarding ? (
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              disabled={submitDisabled || postBookedShowing || halfDayChosen || services.length > 0}
              onClick={() => setRecurring(true)}
              data-testid="quickbook-make-recurring"
            >
              Make it recurring…
            </button>
          ) : null}
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="ferro-btn ferro-btn--primary"
            disabled={submitDisabled || postBookedShowing}
            aria-busy={submitting}
          >
            {submitting ? 'Booking…' : 'Create booking'}
          </button>
        </footer>
      </form>
    </div>
  );
}
